import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Search, Filter, Download, Phone, MessageCircle, Edit2, Trash2, Eye, RefreshCw } from 'lucide-react';
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { StatusBadge, SourceBadge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { useLeads, useCreateLead, useDeleteLead } from '@/hooks/useLeads';
import { useRBAC } from '@/hooks/useRBAC';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate, formatPhone } from '@/utils/formatters';
import type { Lead, LeadStatus, LeadSource } from '@/types';
import { LeadDetailDrawer } from '@/components/shared/LeadDetailDrawer';
import { CreateLeadModal } from '@/components/shared/LeadForm';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New Lead' },
  { value: 'followup', label: 'Follow Up' },
  { value: 'opd_scheduled', label: 'OPD Scheduled' },
  { value: 'opd_done', label: 'OPD Done' },
  { value: 'ipd_scheduled', label: 'IPD Scheduled' },
  { value: 'ipd_done', label: 'IPD Done' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'meta', label: 'Meta (Facebook)' },
  { value: 'google', label: 'Google' },
  { value: 'seo', label: 'SEO' },
  { value: 'gmb', label: 'Google My Business' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'call', label: 'Call' },
  { value: 'website', label: 'Website' },
];

export function LeadsPage() {
  const { can } = useRBAC();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading, refetch } = useLeads({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    source: sourceFilter || undefined,
  });

  const createLead = useCreateLead();
  const deleteLead = useDeleteLead();

  const columns: ColumnDef<Lead>[] = [
    {
      header: 'S.No',
      cell: ({ row }) => (
        <span className="text-slate-400 text-xs font-mono">
          {(page - 1) * 20 + row.index + 1}
        </span>
      ),
      size: 60,
    },
    {
      accessorKey: 'name',
      header: 'Patient',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-slate-900">{row.original.name}</div>
          <div className="text-xs text-slate-400">{row.original.ptId ?? 'No PT-ID'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => <span className="text-sm font-mono">{formatPhone(row.original.phone)}</span>,
    },
    {
      accessorKey: 'disease',
      header: 'Disease',
      cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.disease ?? '—'}</span>,
    },
    {
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => <span className="text-sm text-slate-600">{row.original.location ?? '—'}</span>,
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => <SourceBadge source={row.original.source} />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ row }) => (
        <span className="text-sm text-slate-600">{formatDate(row.original.dueDate)}</span>
      ),
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate({ to: '/leads/$leadId', params: { leadId: row.original.id } })}
            className="p-1.5 text-slate-400 hover:text-brand-cyan hover:bg-cyan-50 rounded-lg transition-colors"
            title="View"
          >
            <Eye size={14} />
          </button>
          <a
            href={`tel:${row.original.phone}`}
            className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Call"
          >
            <Phone size={14} />
          </a>
          <a
            href={`https://wa.me/${row.original.phone?.replace(/\D/g, '')}`}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="WhatsApp"
          >
            <MessageCircle size={14} />
          </a>
          {can('leads') && (
            <button
              onClick={() => {
                if (confirm(`Delete lead "${row.original.name}"?`)) {
                  deleteLead.mutate(row.original.id);
                }
              }}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: data?.pagination.totalPages ?? 1,
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {data?.pagination.total ?? 0} total leads
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" icon={<RefreshCw size={14} />} onClick={() => refetch()}>
            Refresh
          </Button>
          {can('leads') && (
            <Button size="sm" icon={<Plus size={14} />} onClick={() => setShowCreateModal(true)}>
              New Lead
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-48">
            <Input
              placeholder="Search by name, phone, PT-ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              leftIcon={<Search size={14} />}
            />
          </div>
          <div className="w-44">
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-44">
            <Select
              options={SOURCE_OPTIONS}
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              placeholder="All Sources"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-50">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="py-12 text-center">
                    <Spinner className="mx-auto" />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-12 text-center text-slate-400">
                    <div className="text-4xl mb-2">📋</div>
                    <div>No leads found</div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, data.pagination.total)} of {data.pagination.total}
            </span>
            <div className="flex gap-1">
              <Button variant="ghost" size="xs" disabled={!data.pagination.hasPrev} onClick={() => setPage(p => p - 1)}>
                ← Prev
              </Button>
              <span className="flex items-center px-3 text-xs text-slate-600">
                {page} / {data.pagination.totalPages}
              </span>
              <Button variant="ghost" size="xs" disabled={!data.pagination.hasNext} onClick={() => setPage(p => p + 1)}>
                Next →
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => {
            createLead.mutate(data, {
              onSuccess: () => setShowCreateModal(false),
            });
          }}
          isLoading={createLead.isPending}
        />
      )}

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
      />
    </div>
  );
}
